import { Space } from 'antd';
import { snakeCase, startCase } from 'lodash';
import React, { FC } from 'react';
import { useSelector } from 'react-redux';
import { CompanyId } from 'api/models';
import { ResponsibleFilter } from 'components/organisms/tasks/criteria/responsible';
import { MultiSelectFieldFilter } from 'components/organisms/tasks/filters/multi-select';
import { SelectFilterOperationType } from 'components/organisms/tasks/filters/select';
import { RootState } from 'data';
import { TranslationKey, useTranslate } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import { CompaniesStore } from 'stores/companies';
import { removeTrailingDots } from './utils';

const resolve = (
  filter: MultiSelectFieldFilter,
  currentCompanyId: number,
  currentCompanyName: string,
  translate,
): JSX.Element => {
  switch (filter.type) {
    case SelectFilterOperationType.IsOneOf: {
      const values = filter.value ?? [];

      if (values.length === 0) {
        return (
          <>
            <span>
              {removeTrailingDots(
                translate('task_list.filters.is' as TranslationKey),
              )}
            </span>
            <span>
              {translate(
                `task_list.filters.${snakeCase(
                  ResponsibleFilter.All,
                )}` as TranslationKey,
              )}
            </span>
          </>
        );
      }
      if (values.length === 1 && values[0] === currentCompanyId) {
        return (
          <>
            <span>
              {removeTrailingDots(
                translate('task_list.filters.is' as TranslationKey),
              )}
            </span>
            <span>{currentCompanyName}</span>
          </>
        );
      }

      return (
        <>
          <span>
            {removeTrailingDots(
              translate('task_list.filters.is_one_of' as TranslationKey),
            )}
          </span>
          <span>
            {translate(
              `task_list.filters.${snakeCase(
                ResponsibleFilter.NotMyCompany,
              )}` as TranslationKey,
            )}
          </span>
        </>
      );
    }
    default:
      return (
        <>
          <span>
            {removeTrailingDots(
              translate('task_list.filters.is' as TranslationKey),
            )}
          </span>
          <span>
            {translate(
              `task_list.filters.${snakeCase(
                ResponsibleFilter.All,
              )}` as TranslationKey,
            )}
          </span>
        </>
      );
  }
};

export const ResponsibleFilterToText: FC<{
  filter: MultiSelectFieldFilter;
}> = ({ filter }) => {
  const {
    field: { fieldLabel },
  } = filter;
  const {
    user: {
      company: { companyId },
    },
  } = useUser();
  const companiesStore = useSelector(({ companies }: RootState) => companies);
  const { companyName } = CompaniesStore.ofId(CompanyId(companyId))(
    companiesStore,
  );

  const translate = useTranslate();

  return (
    <Space size={4}>
      <span>{startCase(fieldLabel)}</span>
      {resolve(filter, companyId, companyName, translate)}
    </Space>
  );
};
